import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import { Checkbox } from "@veil/ui/components/checkbox";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { ChipInput } from "@/pages/cluster/chip-input";
import type { ClusterResponse } from "@/pages/cluster/types";

// Shape check kept in sync with the backend's permissive HostnameRegex so we
// never reject a value the API would accept — just catch obvious mistakes
// (spaces, protocol prefixes, slashes).
const HOST_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * Step 2: cluster basics.
 *
 * Default (recommended) shape is two hostnames:
 *   - Main hostname  -> serves the client LB (wss /launch, /w/) AND the
 *     manager UI/API on one host (publicHostnames=[main], managerHostname=main).
 *   - Release hostname -> the origin workers pull browser/profile bundles from.
 *
 * The main hostname is required unless the operator opts into IP-only
 * evaluation, which permits an empty hostname and forces TLS to
 * Cloudflare/HTTP (no certificate). Advanced mode exposes the raw split model.
 */
export function ClusterBasicsStep({
	evaluation,
	onEvaluationChange,
	onNext,
	onBack,
}: {
	evaluation: boolean;
	onEvaluationChange: (v: boolean) => void;
	onNext: () => void;
	onBack: () => void;
}) {
	const queryClient = useQueryClient();
	const [name, setName] = useState("veil-cluster");
	const [mainHostname, setMainHostname] = useState("");
	const [releaseHostname, setReleaseHostname] = useState("");
	// Advanced (split-subdomain) fields.
	const [advanced, setAdvanced] = useState(false);
	const [publicHostnames, setPublicHostnames] = useState<string[]>([]);
	const [managerHostname, setManagerHostname] = useState("");
	const [saving, setSaving] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	// Prefill from any existing cluster row so re-entering the wizard doesn't
	// blank the operator's earlier answers. Detect the simple 2-host shape
	// (single public host that also serves the UI) vs. an advanced split.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await (unwrap(
					api.api.cluster.get(),
				) as Promise<ClusterResponse>);
				if (cancelled || !res.cluster) return;
				const c = res.cluster;
				setName(c.name);
				const pub = c.publicHostnames ?? [];
				setPublicHostnames(pub);
				setManagerHostname(c.managerHostname ?? "");
				setReleaseHostname(c.releaseHostname ?? "");
				const isSimple =
					pub.length === 1 &&
					(!c.managerHostname || c.managerHostname === pub[0]);
				if (isSimple) {
					setMainHostname(pub[0] ?? "");
				} else if (pub.length > 1 || c.managerHostname) {
					setAdvanced(true);
				}
			} catch {
				// no cluster yet — keep defaults
			} finally {
				if (!cancelled) setLoaded(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// Inline hostname validation (simple mode). Shape errors surface live;
	// the "required" error waits until a submit attempt.
	const trimmedMain = mainHostname.trim();
	const mainShapeInvalid =
		!advanced && !evaluation && !!trimmedMain && !HOST_RE.test(trimmedMain);
	const mainRequiredError =
		!advanced && !evaluation && submitted && !trimmedMain;
	const mainError = mainRequiredError
		? "Main hostname is required (or turn on IP-only evaluation)."
		: mainShapeInvalid
			? "Enter a valid hostname, e.g. app.example.com."
			: null;

	const releaseError =
		!!releaseHostname.trim() && !HOST_RE.test(releaseHostname.trim())
			? "Enter a valid hostname, e.g. release.example.com."
			: null;

	const onSave = async () => {
		setSubmitted(true);
		if (!name.trim()) {
			toast.error("Cluster name is required");
			return;
		}
		if (releaseError) return;

		let nextPublic: string[];
		let nextManager: string | null;
		const nextRelease = releaseHostname.trim() || null;

		if (evaluation) {
			nextPublic = [];
			nextManager = null;
		} else if (advanced) {
			if (publicHostnames.length === 0) {
				toast.error("Add at least one public hostname");
				return;
			}
			nextPublic = publicHostnames;
			nextManager = managerHostname.trim() || null;
		} else {
			if (!trimmedMain || mainShapeInvalid) return; // inline error shown
			nextPublic = [trimmedMain];
			nextManager = trimmedMain;
		}

		// The release origin must not collide with a public/manager hostname —
		// nginx routes by server_name and the release vhost would shadow the UI.
		if (nextRelease && nextPublic.includes(nextRelease)) {
			toast.error("Release hostname must differ from the main/public hostname");
			return;
		}

		setSaving(true);
		try {
			await unwrap(
				api.api.cluster.patch({
					name: name.trim(),
					publicHostnames: nextPublic,
					managerHostname: nextManager,
					releaseHostname: nextRelease,
				}),
			);
			await queryClient.invalidateQueries({ queryKey: ["cluster"] });
			toast.success("Cluster saved");
			onNext();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Save failed");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div>
			<FieldGroup>
				<Field>
					<FieldLabel htmlFor="cw-name">Cluster name</FieldLabel>
					<Input
						id="cw-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						disabled={!loaded}
					/>
					<FieldDescription>
						An internal label for this cluster (e.g. <code>veil-cluster</code>).
					</FieldDescription>
				</Field>

				{!advanced ? (
					<Field data-invalid={!!mainError}>
						<FieldLabel htmlFor="cw-main">Main hostname</FieldLabel>
						<Input
							id="cw-main"
							value={mainHostname}
							onChange={(e) => setMainHostname(e.target.value)}
							placeholder="app.example.com"
							disabled={!loaded || evaluation}
							aria-invalid={!!mainError}
						/>
						{mainError ? (
							<FieldError>{mainError}</FieldError>
						) : (
							<FieldDescription>
								Serves both the console (UI + API) and client connections (
								<code>wss://&lt;host&gt;/launch</code>) on one domain. Point one
								DNS record here.
							</FieldDescription>
						)}
					</Field>
				) : (
					<>
						<Field>
							<FieldLabel>Public hostname(s)</FieldLabel>
							<ChipInput
								values={publicHostnames}
								onChange={setPublicHostnames}
								disabled={!loaded}
								placeholder="app.example.com"
							/>
							<FieldDescription>
								The hostname(s) clients connect to (
								<code>wss://&lt;host&gt;/launch</code>). Press Enter or comma to
								add.
							</FieldDescription>
						</Field>
						<Field>
							<FieldLabel htmlFor="cw-manager">
								Manager UI hostname (optional)
							</FieldLabel>
							<Input
								id="cw-manager"
								value={managerHostname}
								onChange={(e) => setManagerHostname(e.target.value)}
								placeholder="cluster.example.com"
								disabled={!loaded}
							/>
							<FieldDescription>
								Expose this console under its own domain. Leave blank to keep it
								on the raw host:port link.
							</FieldDescription>
						</Field>
					</>
				)}

				<Field data-invalid={!!releaseError}>
					<FieldLabel htmlFor="cw-release">
						Release origin hostname (optional)
					</FieldLabel>
					<Input
						id="cw-release"
						value={releaseHostname}
						onChange={(e) => setReleaseHostname(e.target.value)}
						placeholder="release.example.com"
						disabled={!loaded}
						aria-invalid={!!releaseError}
					/>
					{releaseError ? (
						<FieldError>{releaseError}</FieldError>
					) : (
						<FieldDescription>
							Where workers pull browser + profile bundles. Use a second DNS
							record. Same-box workers also reach it over the internal network.
						</FieldDescription>
					)}
				</Field>
			</FieldGroup>

			{/* IP-only evaluation opt-out — an inline, clearly-labeled secondary
			    option (not a bare link). Only offered in simple mode. */}
			{!advanced && (
				<label
					htmlFor="cw-eval"
					className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-muted/20 p-3.5"
				>
					<Checkbox
						id="cw-eval"
						checked={evaluation}
						onCheckedChange={(v) => onEvaluationChange(v === true)}
						disabled={!loaded}
						className="mt-0.5"
					/>
					<div className="grid gap-0.5">
						<span className="text-sm font-medium">
							Evaluation (IP only, no TLS)
						</span>
						<span className="text-xs leading-relaxed text-muted-foreground">
							Skip the hostname and run over plain{" "}
							<code>http://&lt;ip&gt;</code> for a quick trial. TLS is forced to
							Cloudflare/HTTP; add a domain later to enable HTTPS.
						</span>
					</div>
				</label>
			)}

			<button
				type="button"
				className="mt-3 rounded-sm text-xs text-muted-foreground underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/60"
				onClick={() => setAdvanced((v) => !v)}
				disabled={!loaded}
			>
				{advanced
					? "Use the simple single-hostname setup"
					: "Advanced: split into separate subdomains"}
			</button>

			<div className="mt-6 flex items-center justify-between gap-2">
				<Button variant="ghost" onClick={onBack} disabled={saving}>
					Back
				</Button>
				<Button onClick={onSave} disabled={saving || !loaded} className="h-11">
					{saving && (
						<Loader2 data-icon="inline-start" className="animate-spin" />
					)}
					Save & continue
				</Button>
			</div>
		</div>
	);
}
